import React from 'react';
import styled from 'styled-components';
import InfoTooltip from '../../Shared/InfoTooltip';

function AnalyticsSummaryCards({ totals }) {
  // Format session time from seconds to human readable format
  const formatSessionTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }

    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0
        ? `${minutes}m ${remainingSeconds}s`
        : `${minutes}m`;
    }

    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  // Engagement Score tooltip content
  const engagementTooltipContent = `Each user is graded from 0 to 100 based on their engagement with the platform. The grading is based on the following criteria:
  • Number of questions answered
  • Number of comments
  • Number of replies
  • Comment/reply length
  • Active time on site
  • Number of returning site visits`;

  const cards = [
    {
      title: 'Total Users',
      value: totals.users,
      description: 'HCP participants',
      icon: '👥'
    },
    {
      title: 'Total Active Time',
      value: formatSessionTime(totals.sessionTime),
      description: 'Combined active time',
      icon: '⏱️'
    },
    {
      title: 'Total Comments',
      value: totals.comments,
      description: 'Comments posted',
      icon: '💬'
    },
    {
      title: 'Total Visits',
      value: totals.visits,
      description: 'Website visits',
      icon: '🚪'
    },
    {
      title: 'Engagement Score',
      value: totals.engagementScore,
      description: 'Average engagement (0-100)',
      icon: '🎯'
    }
  ];

  return (
    <Container>
      {cards.map((card, index) => (
        <Card key={index}>
          <CardHeader>
            <CardIcon>{card.icon}</CardIcon>
            <TitleContainer>
              <CardTitle>{card.title}</CardTitle>
              {card.title === 'Engagement Score' && (
                <InfoTooltip content={engagementTooltipContent} />
              )}
            </TitleContainer>
          </CardHeader>
          <CardValue>{card.value}</CardValue>
          <CardDescription>{card.description}</CardDescription>
        </Card>
      ))}
    </Container>
  );
}

const Container = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media (min-width: 480px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const CardIcon = styled.span`
  font-size: 1.5rem;
`;

const TitleContainer = styled.div`
  align-items: center;
  display: flex;
`;

const CardTitle = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0;
`;

const CardValue = styled.div`
  color: var(--blue);
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

const CardDescription = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  margin: 0;
`;

export default AnalyticsSummaryCards;
